<template>
   <div class="markin-markout-actions-wrapper">
      <div class="mma-wrapper">

        <!-- Row 1: Main controls -->
        <div class="mma-row">

          <div class="actions-left">
            <!-- Play/Pause -->
            <button
              :title="$t('Toggle Play-Pause')+' (spacebar)'"
              class="mma-btn play common-markinmarkout-action-btn"
              @click="playPauseToggle()"
              style="min-width:93px;"
            >
              <span v-if="isPlaying" class="common-play-pause" style="display:flex;align-items:center">
                <svg style="width:18px;height:18px;" xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px" fill="currentColor">
                  <path d="M0 0h24v24H0V0z" fill="none"/>
                  <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                </svg>
                <span>{{$t('Pause')}}</span>
              </span>
              <span v-else class="common-play-pause" style="display:flex;align-items:center">
                <svg style="width:18px;height:18px;" xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px" fill="currentColor">
                  <path d="M0 0h24v24H0V0z" fill="none"/>
                  <path d="M10 8.64L15.27 12 10 15.36V8.64M8 5v14l11-7L8 5z"/>
                </svg>
                <span>{{ $t('Play') }}</span>
              </span>
            </button>
          </div>

          <div class="actions-center">
            <!-- Mark In -->
            <div class="mma-tc-group" :title="$t('Add Markin')+' (i)'">
              <div class="mma-tc-icon" style="color: #eee; font-weight: 100; font-size: 13px; padding-inline-start: 13px; padding-inline-end: 13px;" :title="$t('Capture Markin')+' (i)'" @click="markin()">{{$t('IN')}}</div>
              <input
                type="text"
                ref="markinDurationRef"
                :value="markinDurationValue"
                :placeholder="getMimeType() == 'audio' ? getDurationPlaceholder(audioDuration) : '00:00:00'"
                aria-label="Markin"
                aria-describedby="markin-add-on"
                class="mma-tc-input common-markin-markout-input"
                data-type="markin"
                @input="isValidDurationHandler($event)"
              />
            </div>

            <!-- Mark Out -->
            <div class="mma-tc-group" :title="$t('Add Markout')+' (o)'">
              <input
                type="text"
                ref="markoutDurationRef"
                :value="markoutDurationValue"
                :placeholder="markoutPlaceholderValue"
                aria-label="Markout"
                aria-describedby="markout-add-on"
                class="mma-tc-input common-markin-markout-input"
                data-type="markout"
                @input="isValidDurationHandler($event)"
              />
              <div class="mma-tc-icon right" style="color: #eee; font-weight: 100; font-size: 13px;" :title="$t('Capture Markout')+' (o)'" @click="markout()">{{$t('OUT')}}</div>
            </div>
          </div>

          <div class="actions-right">
            <!-- Preview -->
            <button
              :title="$t('Preview Markin-Markout region')+' (p)'"
              class="mma-btn common-markinmarkout-action-btn preview"
              :class="{ 'mma-btn--active': isActivePreview }"
              @click="playMarkinMarkoutPreview()"
              v-if="isButtonRequired('preview')"
            >
                <template v-if="getMimeType() == 'audio'">
                          <svg  xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor"><path  d="M560-131v-82q90-26 145-100t55-168q0-94-55-168T560-749v-82q124 28 202 125.5T840-481q0 127-78 224.5T560-131ZM120-360v-240h160l200-200v640L280-360H120Zm440 40v-322q47 22 73.5 66t26.5 96q0 51-26.5 94.5T560-320ZM400-606l-86 86H200v80h114l86 86v-252ZM300-480Z"></path></svg>
                          <span style="padding-inline-start: 3px">{{$t('Listen')}}</span>
                    </template>
                    <template v-else>
                        <svg xmlns="http://www.w3.org/2000/svg" height="18px" viewBox="0 0 24 24" width="18px" fill="currentColor"><path d="M0 0h24v24H0V0z" fill="none"/><path d="M12 6.5c3.79 0 7.17 2.13 8.82 5.5-1.65 3.37-5.02 5.5-8.82 5.5S4.83 15.37 3.18 12C4.83 8.63 8.21 6.5 12 6.5m0-2C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zm0 5c1.38 0 2.5 1.12 2.5 2.5s-1.12 2.5-2.5 2.5-2.5-1.12-2.5-2.5 1.12-2.5 2.5-2.5m0-2c-2.48 0-4.5 2.02-4.5 4.5s2.02 4.5 4.5 4.5 4.5-2.02 4.5-4.5-2.02-4.5-4.5-4.5z"/>
                        </svg>
                        <span style="padding-inline-start: 3px;">{{$t('Preview')}}</span>
                    </template>
            </button>

            <!-- Clear -->
            <button
              :title="$t('Clear Markin-Markout region')+' (c)'"
              class="mma-btn common-markinmarkout-action-btn"
              @click="resetMarkinMarkoutSlider()"
              v-if="isButtonRequired('reset')"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" class="bi bi-x" viewBox="0 0 16 16">
                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
              </svg>
              <span>{{$t('Clear')}}</span>
            </button>

            <!-- Subclip -->
            <button
              :title="$t('Capture Frame')+' (f)'"
              class="mma-btn common-markinmarkout-action-btn"
              @click="captureFrame()"
              v-if="isButtonRequired('subclip')"
            >
              <svg xmlns="http://www.w3.org/2000/svg" height="18px" viewBox="0 0 24 24" width="18px" fill="currentColor">
                <path d="M0 0h24v24H0V0z" fill="none"/>
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
              </svg>
              <span>{{$t('Subclip')}}</span>
            </button>

            <button :title="$t('Capture Frame')+' (f)'" v-if="isButtonRequired('captureframe') && (buttonLabelObject && buttonLabelObject.hasOwnProperty('captureframe'))"  style="display:flex;margin-inline-start:0px !important" class="btn btn-outline-secondary" @click="$emit('captureframerequested')"> <svg xmlns="http://www.w3.org/2000/svg" height="18px" viewBox="0 0 24 24" width="18px" fill="currentColor"><path d="M0 0h24v24H0V0z" fill="none"/><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg> <template>{{buttonLabelObject && buttonLabelObject.hasOwnProperty('captureframe') ? buttonLabelObject.captureframe : 'Capture'}}</template></button>

            <!-- Three-dot dropdown -->
            <div class="mma-dropdown" style="position:relative;">
              <div
                class="mma-dots"
                id="bd-versions"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="true"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="25" height="35" fill="currentColor" class="bi bi-three-dots-vertical context-menu" viewBox="0 0 16 16">
                  <path d="M9.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z"/>
                </svg>
              </div>
              <div class="mma-dropdown-menu dropdown-menu dropdown-menu-md-right p-0" aria-labelledby="bd-versions">
                <a v-if="!isAdvancedControlsEnabled" class="mma-dropdown-item dropdown-item p-2" @click="isAdvancedControlsEnabled = true">
                  {{$t('Show Advanced Controls')}}
                </a>
                <a v-if="isAdvancedControlsEnabled" class="mma-dropdown-item dropdown-item p-2" @click="isAdvancedControlsEnabled = false">
                  {{$t('Hide Advanced Controls')}}
                </a>
              </div>
            </div>
          </div>

        </div>

        <!-- Row 2: Advanced controls — outside mma-row, full width -->
        <div v-if="isAdvancedControlsEnabled" class="mma-row mma-row--advanced">
          <button :title="$t('Backward 1 Second')+' (9)'" class="mma-btn common-markinmarkout-action-btn" @click="forwardBackwardVideoBy1Sec('backward')">-1S</button>
          <button :title="$t('Forward 1 Second')+' (0)'"  class="mma-btn common-markinmarkout-action-btn" @click="forwardBackwardVideoBy1Sec('forward')">+1S</button>
          <button :title="$t('Backward 1 Frame')+' (-)'"  class="mma-btn common-markinmarkout-action-btn" @click="forwardBackwardVideoByFrame('backward')">-1F</button>
          <button :title="$t('Forward 1 Frame')+' (=)'"   class="mma-btn common-markinmarkout-action-btn" @click="forwardBackwardVideoByFrame('forward')">+1F</button>
          <button :title="$t('Frame Forward (H - Play backward , L - Play forward)')" class="mma-btn common-markinmarkout-action-btn" :class="fastForwardCurrentSpeed != 1 ? 'mma-btn--active' :''" @click="updateFastwardValue('forward')">FF</button>
          <template v-if="fastForwardCurrentSpeed != 1">
            <button :title="$t('Frame forward speed')" class="mma-btn" style="height: 19px; width: 30px; border-radius: 9px; margin-inline-start: 6px;font-size: 12px; padding: 0px 18px; display: flex ; align-items: center;">{{fastForwardCurrentSpeed}}x</button>
            <button :title="$t('Reset Frame forward')+' (r)'" @click="resetFF()" class="mma-btn" style="height: 22px; width: 41px; font-size: 12px; margin-inline-start: 7px;padding: 0 18px;">{{$t('Reset')}}</button>
          </template>
        </div>

      </div>
  </div>    
</template>

<script>
import {mapActions, mapMutations} from "vuex";
import Utils from '../../components.utils.js';
import {NotifierConstants} from "../../components.constants.js";
export default {
   name:'MarkinMarkoutActions',
   props:["isProgressOutTimeRequired","clearMarker",'emitName','emitPlayActiveRegionName','buttonNotRequiredList','playerObj','sliderReference','buttonLabel','buttonLabelObject','isRequiredForAudioPlayer','fileInfo','audioDuration','isSubclipPropMutationRequired','isManualMarkinMarkoutEmitRequired'],
   data(){
       return {
           isPlaying: false,
           isActivePreview:false,
           markinMarkoutPlayerController:{},
           isAdvancedControlsEnabled:false,
           MILLI_SECOND:1000,
           progressElMap:{},
           videoFrameCallbackID:null,
           frameRate:25,
           markoutDurationValue:'',
           markinDurationValue:'',
           markinMakoutValues:{mark_in:0,mark_out:0},
           sliderElement:{},
           markoutPlaceholderValue:'00:00:00',
           isAudioPlayerType:false,
           fastForwardCurrentSpeed:1,
       }
   },
   methods:{
     fastForwardPlayForward(){
        if(this.fastForwardCurrentSpeed == 0.5){
          this.fastForwardCurrentSpeed = 1;
       }else if(this.fastForwardCurrentSpeed >= 1 && this.fastForwardCurrentSpeed < 5){
          this.fastForwardCurrentSpeed++;
       }
       this.getPlayerObject().playbackRate(this.fastForwardCurrentSpeed);
     },
     fastForwardPlayBackward(){
       console.log("fastForwardPlayBackward fastForwardPlayBackward");
       if(this.fastForwardCurrentSpeed > 0.5 && this.fastForwardCurrentSpeed != 1){
          this.fastForwardCurrentSpeed--;
       }else if(this.fastForwardCurrentSpeed == 1){
          this.fastForwardCurrentSpeed = 0.5;
       }
       this.getPlayerObject().playbackRate(this.fastForwardCurrentSpeed);
     },
     resetFF(){
        this.fastForwardCurrentSpeed = 1;
        this.getPlayerObject().playbackRate(this.fastForwardCurrentSpeed);
     },
     updateFastwardValue(speedAction){
         if(speedAction == 'forward'){
                if(this.fastForwardCurrentSpeed == 0.5){
                    this.fastForwardCurrentSpeed = 1;
                }else if(this.fastForwardCurrentSpeed > 0.5 && this.fastForwardCurrentSpeed < 5){
                    this.fastForwardCurrentSpeed++
                }else if(this.fastForwardCurrentSpeed >= 5){
                    this.fastForwardCurrentSpeed = 0.5;
                }
            }
          this.getPlayerObject().playbackRate(this.fastForwardCurrentSpeed);
      
    },
    getMimeType(){
      if(this.fileInfo){
        return Utils.getFileMediaType(this.fileInfo);
      }
      return '';
    },
    setMarkoutDurationValue(value){
        this.markoutDurationValue = value;
        this.markinMakoutValues.mark_out = value;
    },
    setMarkinLabelValue(whereYouAt){
        this.markinDurationValue = Utils.getExactDuationInSeconds(whereYouAt);
    },
    setMarkoutLabelValue(whereYouAt){
        this.markoutDurationValue = Utils.getExactDuationInSeconds(whereYouAt);
    },
    setMarkinLabelValueWithFF(whereYouAt){
        let videoDuration;
        try{
           videoDuration = this.getPlayerObject().duration()
        }catch(e){console.log(e)}
        this.markinDurationValue = Utils.getDurationBasedOnVideoDuration(whereYouAt,videoDuration,this.frameRate,'.');
        this.markinMakoutValues.mark_in = whereYouAt;
    },
    setMarkoutLabelValueWithFF(whereYouAt){
        let videoDuration;
        try{
           videoDuration = this.getPlayerObject().duration()
        }catch(e){console.log(e)}
        this.markoutDurationValue = Utils.getDurationBasedOnVideoDuration(whereYouAt,videoDuration,this.frameRate,'.');
        this.markinMakoutValues.mark_out = whereYouAt;
    },
    setMarkinDurationValue(value){
        this.markinDurationValue = value;
        this.markinMakoutValues.mark_in = value;
    },
    setPlayPauseState(state){
        this.isPlaying = state;
    },
    clearAudioMarkers(duration = 0){
        this.markoutPlaceholderValue = Utils.getExactDuationInSeconds(duration);
        if(Utils.getFileMediaType(this.fileInfo) == 'audio'){
          this.markoutPlaceholderValue = this.formatDurationHHMMSS_withoutFrame(this.audioDuration);
        }
        this.markoutDurationValue = ''; 
        this.markinDurationValue = ''; 
        this.markinMakoutValues.mark_in = 0;
        this.markinMakoutValues.mark_out = 0;
        this.$refs.markinDurationRef.classList.remove('active-markin-markout-duration');
        this.$refs.markinDurationRef.classList.remove('invalid-duration-value');
        this.$refs.markoutDurationRef.classList.remove('active-markin-markout-duration');
        this.$refs.markoutDurationRef.classList.remove('invalid-duration-value');
    },
    /*
    isValidDurationHandler(event){
       console.log('event',event.target.value,Utils.getFileMediaType(this.fileInfo) == 'audio');
       let isValid, durationInRange , totalDuration;
       if(this.fileInfo && Utils.getFileMediaType(this.fileInfo) == 'audio'){
           totalDuration = this.audioDuration;
           isValid = Utils.isValidDurationWithoutFrame(event.target.value,this.audioDuration);
           durationInRange = Utils.hhOrmmssWithoutFrame(event.target.value,this.audioDuration);
       }else{
            totalDuration = this.getPlayerObject().duration();
            isValid = Utils.isValidDuration(event.target.value,this.getPlayerObject().duration(),this.frameRate);
            durationInRange = Utils.hhmmssffToMs(event.target.value,this.getPlayerObject().duration(),this.frameRate);
       }

       console.log(`durationInRange`,durationInRange,'totalDuration',totalDuration,'isValid',isValid);
       if(isValid && durationInRange <= totalDuration){
           event.target.classList.remove('invalid-duration-value');
           event.target.classList.add('active-markin-markout-duration');
           if(event.target.getAttribute('data-type') == 'markin'){
              this.markinDurationValue = event.target.value;
              this.markinMakoutValues.mark_in = durationInRange;
              if(this.$refs.markoutDurationRef.classList.contains('active-markin-markout-duration')){
                   if(this.markinMakoutValues.mark_out < this.markinMakoutValues.mark_in){
                      this.$refs.markinDurationRef.classList.add('invalid-duration-value');
                   }else{
                      if(this.fileInfo && Utils.getFileMediaType(this.fileInfo) == 'audio'){
                         this.setMarkinDurationValue(durationInRange * this.MILLI_SECOND);
                         this.setMarkinLabelValue(durationInRange);
                         this.$emit("audioEvent",'setMarkinValue',parseInt(durationInRange * this.MILLI_SECOND));
                      }else{
                         this.$set(this.getSliderComponent().value,0,parseInt(durationInRange * this.MILLI_SECOND));
                      }
                   }
              }else{
                    if(this.fileInfo && Utils.getFileMediaType(this.fileInfo) == 'audio'){
                         this.setMarkinDurationValue(durationInRange * this.MILLI_SECOND);
                         this.setMarkinLabelValue(durationInRange);
                         this.$emit("audioEvent",'setMarkinValue',parseInt(durationInRange * this.MILLI_SECOND));
                    }else{
                       this.$set(this.getSliderComponent().value,0,parseInt(durationInRange * this.MILLI_SECOND));
                    }
              }
           }
           if(event.target.getAttribute('data-type') == 'markout'){
                this.markoutDurationValue = event.target.value;
                this.markinMakoutValues.mark_out = durationInRange;
               if(this.markinMakoutValues.mark_in > this.markinMakoutValues.mark_out){
                    this.$refs.markinDurationRef.classList.remove('active-markin-markout-duration');
                    this.$refs.markinDurationRef.classList.add('invalid-duration-value');
               }else{
                    if(this.fileInfo && Utils.getFileMediaType(this.fileInfo) == 'audio'){
                         this.setMarkoutDurationValue(durationInRange * this.MILLI_SECOND);
                         this.setMarkoutLabelValue(durationInRange);
                         this.$emit("audioEvent",'setMarkoutValue',parseInt(durationInRange * this.MILLI_SECOND));
                    }else{
                        this.$set(this.getSliderComponent().value,1,parseInt(durationInRange * this.MILLI_SECOND));
                    }
               }
           }
       }else{
          event.target.classList.remove('active-markin-markout-duration');
          event.target.classList.add('invalid-duration-value');
       }
     },
     */
     isValidDurationHandler(event) {
        let isValid, durationInRange, totalDuration;
        const isAudio = this.fileInfo && Utils.getFileMediaType(this.fileInfo) == 'audio';

        if (isAudio) {
            totalDuration = this.audioDuration;
            isValid = Utils.isValidDurationWithoutFrame(event.target.value, this.audioDuration);
            durationInRange = Utils.hhOrmmssWithoutFrame(event.target.value, this.audioDuration); // seconds
        } else {
            totalDuration = this.getPlayerObject().duration();
            isValid = Utils.isValidDuration(event.target.value, this.getPlayerObject().duration(), this.frameRate);
            durationInRange = Utils.hhmmssffToMs(event.target.value, this.getPlayerObject().duration(), this.frameRate);
        }

        console.log("durationInRange durationInRange:",durationInRange);

        if (isValid && durationInRange <= totalDuration) {
            event.target.classList.remove('invalid-duration-value');
            event.target.classList.add('active-markin-markout-duration');

            if (event.target.getAttribute('data-type') == 'markin') {
                this.markinDurationValue = event.target.value;

                if (this.$refs.markoutDurationRef.classList.contains('active-markin-markout-duration')) {
                    if (this.markinMakoutValues.mark_out !== null && this.markinMakoutValues.mark_out < durationInRange) {
                        this.$refs.markinDurationRef.classList.remove('active-markin-markout-duration');
                        this.$refs.markinDurationRef.classList.add('invalid-duration-value');
                    } else {
                        this.$refs.markinDurationRef.classList.remove('invalid-duration-value');
                        if (isAudio) {
                            this.setMarkinDurationValue(durationInRange * this.MILLI_SECOND);
                            //this.setMarkinLabelValue(durationInRange);
                            this.markinDurationValue = this.formatDurationHHMMSS_withoutFrame(durationInRange);
                            this.$emit("audioEvent", 'setMarkinValue', parseInt(durationInRange * this.MILLI_SECOND));
                        } else {
                            this.$set(this.getSliderComponent().value, 0, parseInt(durationInRange * this.MILLI_SECOND));
                            if(this.isManualMarkinMarkoutEmitRequired){
                                this.$emit("manualMarkinSuccess",durationInRange);
                            }
                        }
                    }
                } else {
                    if (isAudio) {
                        this.setMarkinDurationValue(durationInRange * this.MILLI_SECOND);
                        //this.setMarkinLabelValue(durationInRange);
                        this.markinDurationValue = this.formatDurationHHMMSS_withoutFrame(durationInRange);
                        this.$emit("audioEvent", 'setMarkinValue', parseInt(durationInRange * this.MILLI_SECOND));
                    } else {
                        this.$set(this.getSliderComponent().value, 0, parseInt(durationInRange * this.MILLI_SECOND));
                        if(this.isManualMarkinMarkoutEmitRequired){
                            this.$emit("manualMarkinSuccess",durationInRange);
                         }
                    }
                }
                // set AFTER setter calls so it can't be overwritten with ms
                this.markinMakoutValues.mark_in = durationInRange; // seconds
            }

            if (event.target.getAttribute('data-type') == 'markout') {
                this.markoutDurationValue = event.target.value;

                if (this.markinMakoutValues.mark_in !== null && this.markinMakoutValues.mark_in > durationInRange * this.MILLI_SECOND) {
                    this.$refs.markinDurationRef.classList.remove('active-markin-markout-duration');
                    this.$refs.markinDurationRef.classList.add('invalid-duration-value');
                } else {
                    this.$refs.markinDurationRef.classList.remove('invalid-duration-value');
                    if (isAudio) {
                        this.setMarkoutDurationValue(durationInRange * this.MILLI_SECOND);
                        //this.setMarkoutLabelValue(durationInRange);
                        this.markoutDurationValue = this.formatDurationHHMMSS_withoutFrame(durationInRange);
                        this.$emit("audioEvent", 'setMarkoutValue', parseInt(durationInRange * this.MILLI_SECOND));
                    } else {
                        this.$set(this.getSliderComponent().value, 1, parseInt(durationInRange * this.MILLI_SECOND));
                        if(this.isManualMarkinMarkoutEmitRequired){
                            this.$emit("manualMarkoutSuccess",true,parseInt(durationInRange * this.MILLI_SECOND));
                         }
                    }
                }
                // set AFTER setter calls so it can't be overwritten with ms
                this.markinMakoutValues.mark_out = durationInRange; // seconds
            }
        } else {
            event.target.classList.remove('active-markin-markout-duration');
            event.target.classList.add('invalid-duration-value');
        }
    },
    resetActivePreview(){
      this.isActivePreview = false;
    },
    isElementVisible(el) {
           return !!( el.offsetWidth || el.offsetHeight || el.getClientRects().length );
    },
    getPlayerObject(){
      if(this.sliderReference && this.sliderReference.videoPlayerRef){
          return this.sliderReference.videoPlayerRef.player;
      }
      if(this.playerObj && Object.keys(this.playerObj).length){
          return this.playerObj;
      }
      return this.$parent.$parent.playerObj;
    },

    getSliderComponent(){
        // console.log('this.sliderReference',this.sliderReference);
        if(this.sliderReference && Object.hasOwnProperty.call(this.sliderReference,'sliderComponentRef')){
            return this.sliderReference.sliderComponentRef;
        }
        return this.$parent;
    },

    isButtonRequired(name){
         if(!this.buttonNotRequiredList) return true;
         if(this.buttonNotRequiredList && Array.isArray(this.buttonNotRequiredList) && this.buttonNotRequiredList.length === 0) return true;
         if(this.buttonNotRequiredList.indexOf(name) > -1) return false;
         return true;
    },

    displayThumbnail(videoRef){
        let video = document.querySelector(`#${videoRef} video`);
        console.log('video',video,'this.makinmarkout.videoRef',videoRef);
        let videoCanvas = document.getElementById(`canvas_${this.canvasId}`);
        console.log('videoCanvas',videoCanvas,'this.canvasId',this.canvasId);
        videoCanvas.getContext("2d").drawImage(video, 0, 0, 180, 130);
        try{
         return videoCanvas.toDataURL();
        }catch(e){
            console.log(e);
        }
        return '';
    },

    getComponentByName(name){
       let foundComp = this;
       while(foundComp){
            foundComp = foundComp.$parent;
        if(!foundComp) break;
        let comp =  foundComp['$options']['_componentTag'] || String(foundComp.$vnode.data.ref).replace('modal','');
        if(foundComp &&  comp == name) break;
       }
       return foundComp;
    },

    getComponent(componentName) {
        let component = null
        let parent = this.$parent
        while (parent && !component) {
            if (parent.$options.name === componentName) {
               component = parent
            }
            parent = parent.$parent
        }
        return component
    },
    playPauseToggle(){
        console.log('this....',this);
         if(this.isAudioPlayerType){
            this.$emit("audioEvent",'stopPlayPause')
            return
         }
        if(this.getPlayerObject().paused()){
            this.getPlayerObject().play();
            this.isPlaying = true;
        }else{
            this.getPlayerObject().pause();
            this.isPlaying = false;
        }
    },

    forwardBackwardVideoByFrame(type){
            if (!type) {
                console.log("Invalid type");
                return false;
            }
            if(this.isAudioPlayerType){
                this.$emit("audioEvent",'forwardBackwardVideoByFrame',type)
                return
            }
            if (this.getPlayerObject()) {
                var dur = this.getPlayerObject().duration(); //full duration of video
                var curDur = this.getPlayerObject().currentTime(); //current time of a video
                if(dur && type == "forward"  && curDur == 0){
                    this.getPlayerObject().currentTime(curDur + 0.04 + 0.001);
                    this.getSliderComponent().setPositionFromValue((curDur + 0.04 + 0.001) * this.MILLI_SECOND);
                    return;
                }
                if (dur != undefined && curDur != undefined) {
                    //forward 1 second
                    if (type == "forward" && curDur < (dur + 0.04)) {
                        this.getPlayerObject().currentTime(curDur + 0.04)
                       // Utils.getExactDuationInSeconds(curDur + 0.04);
                        this.getSliderComponent().setPositionFromValue((curDur + 0.04) * this.MILLI_SECOND);
                    }

                    //backward 1 second
                    if (type == "backward" && curDur > 0.04) {
                        this.getPlayerObject().currentTime(curDur - 0.04)
                        // Utils.getExactDuationInSeconds(curDur - 0.04);
                         this.getSliderComponent().setPositionFromValue((curDur - 0.04) * this.MILLI_SECOND);
                    }
                }
            }
    },

    forwardBackwardVideoBy1Sec(action) {
        if (!action) {
            console.log("Invalid action");
            return false;
        }
        if(this.isAudioPlayerType){
            this.$emit("audioEvent",'forwardBackwardVideoBy1Sec',action)
            return
        }
        if (this.getPlayerObject()) {
            var dur = this.getPlayerObject().duration(); //full duration of video
            var curDur = this.getPlayerObject().currentTime(); //current time of a video
            if (dur != undefined && curDur != undefined) {
                //forward 1 second
                if (action == "forward" && curDur < (dur + 1)) {
                    this.getPlayerObject().currentTime(curDur + 1);
                    this.getSliderComponent().setPositionFromValue((curDur + 1) * this.MILLI_SECOND);
                }
    
                //backward 1 second
                if (action == "backward" && curDur > 1) {
                    this.getPlayerObject().currentTime(curDur - 1)
                    this.getSliderComponent().setPositionFromValue((curDur - 1) * this.MILLI_SECOND);
                }
            }
        }
    
    },

    markin(){
        if(this.isAudioPlayerType){
            this.$emit("audioEvent",'addMarkin')
            return
        }
       if(this.getPlayerObject()){
          let whereYouAt = parseInt(this.getPlayerObject().currentTime() * this.MILLI_SECOND);
          this.markinDurationValue = Utils.getDurationBasedOnVideoDuration(whereYouAt / this.MILLI_SECOND,this.getPlayerObject().duration(),this.frameRate,'.');
          try{
            if(whereYouAt > this.getSliderComponent().value[1]){
                this.markoutDurationValue = Utils.getDurationBasedOnVideoDuration(this.getSliderComponent().options.max / this.MILLI_SECOND,this.getPlayerObject().duration(),this.frameRate,'.');
                this.markoutPlaceholderValue = this.markoutDurationValue;
                this.$set(this.getSliderComponent().value,1,this.getSliderComponent().options.max);
            }
          }catch(e){console.log(e)}
          this.$set(this.getSliderComponent().value,0,whereYouAt);
          this.$emit('captureMarkINclicked');
       }
    },

    markout(){
      if(this.isAudioPlayerType){
            this.$emit("audioEvent",'addMarkout')
            return
       }
      if(this.getPlayerObject()){
        let whereYouAt = parseInt(this.getPlayerObject().currentTime() * this.MILLI_SECOND);
        this.markoutDurationValue = Utils.getDurationBasedOnVideoDuration(whereYouAt / this.MILLI_SECOND,this.getPlayerObject().duration(),this.frameRate,'.');
        this.markoutPlaceholderValue = this.markoutDurationValue;
        try{
            if(!this.markinDurationValue){
                this.markinDurationValue = Utils.getDurationBasedOnVideoDuration(this.getSliderComponent().value[0] / this.MILLI_SECOND,this.getPlayerObject().duration(),this.frameRate,'.');
            }
        }catch(e){console.log(e)}
        console.log('whereYouAt',whereYouAt);
        this.$set(this.getSliderComponent().value,1,whereYouAt);
        // this.$parent.value[1] = Number(whereYouAt.toFixed(1));
          this.getPlayerObject().pause();
          this.$emit('captureMarkOUTclicked');
        }
    },

    getThumbnailDisplayDuration(){
        let videoDuration1 = this.$parent.playerDuration;
        let videoDuration = String(videoDuration1).slice(0,7);
        console.log("=============", videoDuration)
        let count = videoDuration.indexOf(".");
        console.log("===============", count)
        count = 1 - count;
        console.log("===============", count)
        count = Math.pow(10, 2);
        console.log("===============", count)
        let durationId = "00".concat(String(videoDuration * 2));
        console.log("*******", durationId);

        return durationId;
    },

/*     captureFrame(){
        if(this.getPlayerObject()){
            let parentC = this.getComponentByName(this.$parent.$parent.$attrs.wrappercomponent);
             parentC.$set(parentC.markinMarkoutThumbnailObj,'show',true);
             parentC.$set(parentC.markinMarkoutThumbnailObj,'videoRef',this.getPlayerObject().id_);
        }
    }, */

    postAssetData(thumbnail){
            let payload = {};
            payload['slug_name'] = '';
            payload['category_id'] = null;
            payload['asset_type'] = 'subclip';
           // payload['markers'] = {start:parseInt(this.$parent.value[0]),end:parseInt(this.$parent.value[1]),frame_thumbnail_uri:thumbnail};
            payload['markers'] = {...this.$parent.getStartEndTime(),frame_thumbnail_uri:thumbnail};
            payload['master_asset_id'] = null;
            Object.assign(payload,this.$parent.$parent.$attrs);
            delete payload.wrappercomponent;
            let parentC = this.getComponentByName(this.$parent.$parent.$attrs.wrappercomponent);
            console.log('tagmetdaata wrapper',parentC);
            let slugName = parentC.assetDetails.slug_name + '_' + parseInt(this.$parent.value[0]) + '_' + parseInt(this.$parent.value[1]);
            payload.slug_name = slugName;
            this.insertSubClip(payload).then((res) =>{
             if(this.emitName)
                this.$emit(this.emitName,res)
                parentC.markinMarkoutThumbnailObj.frames[res.upserted_data._id] = res.upserted_data;
            });
    },

    captureFrame(event, callback){
             console.log('event',event);
             if(this.isAudioPlayerType){
                this.$emit("audioEvent",'addSubclip');
                return;
             }
             if(!this.isSubclipPropMutationRequired){
                let parentC = this.getComponentByName(this.$parent.$parent.$attrs.wrappercomponent);
                console.log("parentC------------------------",parentC, this.$route, this.$parent)
                let slugName = parentC.assetDetails.slug_name + this.getThumbnailDisplayDuration();
                parentC.$set(parentC.markinMarkoutThumbnailObj,'dur',`${Utils.getDurationInSeconds(this.$parent.value[0])} - ${Utils.getDurationInSeconds(this.$parent.value[1])}`);
                parentC.$set(parentC.markinMarkoutThumbnailObj,'filename',slugName);
                parentC.$set(parentC.markinMarkoutThumbnailObj,'show',true);
                parentC.$set(parentC.markinMarkoutThumbnailObj,'videoRef',this.getPlayerObject().id_);
                if(parentC.markinMarkoutThumbnailObj.capture == false) parentC.markinMarkoutThumbnailObj.capture = true;
                if(callback && typeof callback == 'function'){
                    callback();
                }
             }else{
                this.$emit("showSubclipMetadata");
             }
          /*
            payload["proxy_info"] = {"thumbnail_uri":thumbnail} 
             
            console.log('payload payload',payload); 
    
            this.insertSubClip(payload).then((res) =>{
               console.log('res',res);
                this.$emit("subclipPostResponse",res)
                parentC.markinMarkoutThumbnailObj.frames[res.upserted_data._id] = res.upserted_data;
            });
         } */
    },

    resetMarkinMarkoutSlider(){
        this.isActivePreview = false;
        if(this.isAudioPlayerType){
            this.$emit("audioEvent",'clear');
            return
        }
        this.getSliderComponent().value.length = 0;
        this.getSliderComponent().value.push(0);
        this.getSliderComponent().value.push(this.getSliderComponent().options.max);
        this.getPlayerObject().currentTime(0);
        // document.querySelector('.custom-tooltip').classList.remove('move-marker-to-left');
        this.markoutPlaceholderValue = Utils.getExactDuationInSeconds(this.getPlayerObject().duration() ,this.frameRate, '.');
        this.markoutDurationValue = ''; //Utils.getDurationFormat(this.getPlayerObject().duration());
        this.markinDurationValue = ''; //Utils.getDurationFormat(this.getPlayerObject().duration());
        this.markinMakoutValues.mark_in = 0;
        this.markinMakoutValues.mark_out = 0;

        this.$refs.markinDurationRef.classList.remove('active-markin-markout-duration');
        this.$refs.markinDurationRef.classList.remove('invalid-duration-value');
        this.$refs.markoutDurationRef.classList.remove('active-markin-markout-duration');
        this.$refs.markoutDurationRef.classList.remove('invalid-duration-value');
        this.sliderReference.sliderComponentRef.setPositionFromValue(0);
        this.$emit("clearedMarkIN_OUT");
    },

    playMarkinMarkoutPreview(playOnEachClick = false){
        this.isActivePreview = true;
     
     if(this.isAudioPlayerType){
            this.$emit("audioEvent",'playMarkinMarkoutPreview');
            return
      }
      
      try{
         this.$refs.markinDurationRef.classList.remove('invalid-duration-value');
         this.$refs.markinDurationRef.classList.remove('active-markin-markout-duration');
         this.$refs.markoutDurationRef.classList.remove('invalid-duration-value');
         this.$refs.markoutDurationRef.classList.remove('active-markin-markout-duration');
      }catch(e){console.log(e)}

     if(this.emitPlayActiveRegionName)
        this.$emit(this.emitPlayActiveRegionName,true);
         
        if(this.getPlayerObject().paused())
             this.getPlayerObject().currentTime(Number(this.getSliderComponent().value[0] / this.MILLI_SECOND))

        if(this.getPlayerObject().paused())
            this.getPlayerObject().play();

        if(playOnEachClick){
             this.getPlayerObject().currentTime(Number(this.getSliderComponent().value[0] / this.MILLI_SECOND))
             this.getPlayerObject().play();
        }

    },

    setCurrentTimeMarker(value){
       this.getSliderComponent().setPositionFromValue(parseInt(value));
    },

    playerListener(){
        try{this.getPlayerObject().off('timeupdate')}catch(e){console.log(e)}
        this.getPlayerObject().on('timeupdate',() =>{
            try{
              this.getSliderComponent().$refs.cursorSliderValue.style.cssText += 'visibility: hidden;';
            }catch(e){console.log(e)}
            let time = this.getPlayerObject().currentTime();
            if(this.isProgressOutTimeRequired)
               this.updatePlayerProgress(time);
            if(!this.isActivePreview) return false;
             let currentTime = time * this.MILLI_SECOND;
            //   console.log('currentTime',currentTime);
            if(currentTime >= this.getSliderComponent().value[1]){
                // this.getPlayerObject().pause();
                this.isActivePreview = false;
                if(this.emitPlayActiveRegionName)
                   this.$emit(this.emitPlayActiveRegionName,false);
            }
        });

        this.getPlayerObject().on("pause", ()=>{
            this.isPlaying = false
        });

        this.getPlayerObject().on("play",  ()=> {
            this.isPlaying = true
        });

        try{
            this.setProgressLabel(this.getPlayerObject().duration());
            this.markinMakoutValues.mark_out = this.getPlayerObject().duration() * this.MILLI_SECOND;
            this.markoutPlaceholderValue = Utils.getExactDuationInSeconds(this.getPlayerObject().duration() , this.frameRate, '.');
            

          this.videoFrameCallbackID = this.getPlayerObject().requestAnimationFrame(this.handlePlayerTimeUpdate);
          this.getPlayerObject().off("pause",this.handleVideojsOnPause);
          this.getPlayerObject().on("pause",this.handleVideojsOnPause);
          this.getPlayerObject().off("play",this.handleVideojsOnPlay);
          this.getPlayerObject().on("play",this.handleVideojsOnPlay);
        }catch(e){console.log(e)}

      if(Utils.getFileMediaType(this.fileInfo) == 'audio'){
          this.markoutPlaceholderValue = this.formatDurationHHMMSS_withoutFrame(this.audioDuration);
      }
    },

    updatePlayerProgress(time){
        if(this.sliderReference && this.sliderReference.videoPlayerRef)
           this.sliderReference.videoPlayerRef.$el.querySelector('.player-current-time-division .currrent-time').textContent = Utils.getExactDuationInSeconds(time , this.getSliderComponent().frameRate, '.');
    },

    formatDurationHHMMSS_or_MMSS(duration) {
      return Utils.getExactDuationInSeconds(duration,this.frameRate,'.');
    },

    formatDurationHHMMSS_withoutFrame(duration) {
      return Utils.getDurationInHHorMMSS(duration);
    },

    getDurationPlaceholder(duration) {
        return duration >= 60 * 60 ? '00:00:00' : '00:00';
    },

    getDurationFormat(duration){
        const date = new Date(duration * 1000); // convert to milliseconds
        const hours = date.getUTCHours();
        if(hours > 0){
            return '00:00:00.00'
        }else{
            return '00:00.00'
        }
    },

    setProgressLabel(duration){
       try{
        [].forEach.call(document.querySelectorAll(`.player-current-time-division`),(el) => {
            el.remove();
        });
       }catch(e){console.log(e)}
       let progressBar = this.sliderReference.videoPlayerRef.$el.querySelector('.vjs-control-bar');
       let currentProgressEl = document.createElement('div');
       currentProgressEl.classList.add('player-current-time-division');
       currentProgressEl.innerHTML = `<span class="currrent-time">${this.getDurationFormat(duration)}</span> <span class="time-division-label">/</span> <span class="full-time-of-time-division">${this.formatDurationHHMMSS_or_MMSS(duration)}</span>`;
       progressBar.insertBefore(currentProgressEl,progressBar.childNodes[2]);
    },

    handleVideojsOnPlay(){
        this.handleVideojsOnPause();
        this.videoFrameCallbackID = this.getPlayerObject().requestAnimationFrame(this.handlePlayerTimeUpdate);
    },

    handleVideojsOnPause(){
        try{
            if(this.videoFrameCallbackID) {
                console.log(`Cancelled old [videoFrameCallback][${this.videoFrameCallbackID}]`);
                this.getPlayerObject().cancelAnimationFrame(this.videoFrameCallbackID);
            }
        }catch(e){console.log(e)}
    },

    handlePlayerTimeUpdate(){
      if(!this.getPlayerObject().paused()){
        if(!Object.keys(this.progressElMap).length){ // to avoid DOM operation of finding element we kept in global variable
            this.progressElMap['el'] = this.sliderReference.videoPlayerRef.$el.querySelector('.player-current-time-division .currrent-time')
        }
        // console.log('player current time:',this.getPlayerObject().currentTime())
        this.progressElMap.el.textContent = Utils.getExactDuationInSeconds(this.getPlayerObject().currentTime() , this.frameRate, '.');
        this.setCurrentTimeMarker(this.getPlayerObject().currentTime() * this.MILLI_SECOND);
        this.getPlayerObject().requestAnimationFrame(this.handlePlayerTimeUpdate);
        if(this.isActivePreview){
            if(this.getPlayerObject().currentTime() * this.MILLI_SECOND >= this.getSliderComponent().value[1]){
                this.getPlayerObject().pause();
            }
        }
       }else{
         try{
            if(this.videoFrameCallbackID) {
                console.log(`Cancelled old [videoFrameCallback][${this.videoFrameCallbackID}]`);
                this.getPlayerObject().cancelAnimationFrame(this.videoFrameCallbackID);
            }
          }catch(e){console.log(e)}
       }
    },

    setSubclipMarkersBasedOnVideoDuration(invalue,outvalue){
        this.getSliderComponent().value.length = 0;
        this.getSliderComponent().value.push(parseInt(invalue));
        this.getSliderComponent().value.push(parseInt(outvalue));

        let totalDur = null;
        try{
           totalDur = this.getPlayerObject().duration();
        }catch(e){console.warn('Total duration not available:',e)}

        this.markoutDurationValue = Utils.getDurationBasedOnVideoDuration(outvalue / this.MILLI_SECOND,totalDur,this.frameRate,'.');
        this.markinDurationValue =  Utils.getDurationBasedOnVideoDuration(invalue / this.MILLI_SECOND,totalDur,this.frameRate,'.');
        this.markinMakoutValues.mark_in = invalue;
        this.markinMakoutValues.mark_out = outvalue;
    },

    setMarkers(invalue,outvalue){
        
        this.getSliderComponent().value.length = 0;
        this.getSliderComponent().value.push(parseInt(invalue));
        this.getSliderComponent().value.push(parseInt(outvalue));
    /*     try{
            this.getSliderComponent().getRightmostSliderClass();
        }catch(e){console.log(e)} */

        this.markoutDurationValue = Utils.getExactDuationInSeconds(outvalue / this.MILLI_SECOND,this.frameRate,'.');
        this.markinDurationValue =  Utils.getExactDuationInSeconds(invalue / this.MILLI_SECOND,this.frameRate,'.');
        this.markinMakoutValues.mark_in = invalue;
        this.markinMakoutValues.mark_out = outvalue;
    },

    clearOldMarkinMarkoutValues(timeObj){
        this.markoutDurationValue = '';
        this.markinDurationValue =  '';
        if(timeObj && timeObj.end)
             this.markoutPlaceholderValue = Utils.getExactDuationInSeconds(timeObj.end,this.frameRate,'.');
    },

    handleGlobalSettingsUpdateNotificationMarkinMarkout(notification){
        notification = notification.detail && Object.keys(notification.detail).length!=0?notification.detail:notification;
        if(Object.keys(notification.message).includes('media_edit_advanced_controls')){
            this.isAdvancedControlsEnabled = notification.message.media_edit_advanced_controls;
        }
    },

    handleKeyBoardShortcuts(event){
        let keyCode = event.keyCode || event.which;
        console.log(keyCode,event);
        let pressedElement = String(event.target.nodeName).toLowerCase();
        if(pressedElement == 'input' || pressedElement == 'textarea' || pressedElement == 'select' ) return true;
        console.log('execute keycode');

        if(keyCode === 73){ // 'i' key for markin
             this.markin();
        } 

        if(keyCode === 79){ // 'o' key for markout
            this.markout();
        } 
     
        if(keyCode === 80){ // 'p' key for preview markin,markout region [video]
              this.playMarkinMarkoutPreview();
        } 

        if(keyCode === 32){ // 'spacebar' key for toggle play->pause
            this.playPauseToggle();
        } 

        if(keyCode === 57){ // '9' key for backward video by 1sec
             this.forwardBackwardVideoBy1Sec('backward');
        } 

        if(keyCode === 48){ // '0' key for forward video by 1sec
           this.forwardBackwardVideoBy1Sec('forward'); 
        } 

        if(keyCode === 189){ // '-' key for backward video by 1 frame
            this.forwardBackwardVideoByFrame('backward');
        }

        if(keyCode === 187){ // '=' key for forward video by 1 frame
             this.forwardBackwardVideoByFrame('forward');
        }

        if(keyCode === 67){ // 'c' key for clearing markin,markout region
            this.resetMarkinMarkoutSlider();
        }

        if(keyCode === 70){ // 'f' key for clearing markin,markout region
            this.captureFrame();
        }

    },
   ...mapActions("assets", ["insertSubClip"]),
   ...mapMutations("websocket",["addWSEventListener", "removeWSEventListener"]),
  },

  mounted(){
    if(!this.isAudioPlayerType){
        try{
          this.playerListener();
        }catch(e){console.log('e:',e)}
        console.log(`globalSettingsFromStartup`,this.globalSettingsFromStartup);
        try{
            if(this.globalSettingsFromStartup && Object.prototype.hasOwnProperty.call(this.globalSettingsFromStartup,'media_edit_advanced_controls')){
                this.isAdvancedControlsEnabled = this.globalSettingsFromStartup.media_edit_advanced_controls;
            }
        }catch(e){console.log(e)}
        // window.addEventListener('keydown', this.handleKeyBoardShortcuts);
        this.markinMarkoutPlayerController = {
            playerListener:this.playerListener,preview:this.playMarkinMarkoutPreview,
            setMarkers:this.setMarkers,postAssetData:this.postAssetData,clearMakinMarkout:this.resetMarkinMarkoutSlider,
            resetActivePreview:this.resetActivePreview,
            setCurrentTimeMarker:this.setCurrentTimeMarker,
            setSubclipMarkersBasedOnVideoDuration: this.setSubclipMarkersBasedOnVideoDuration
         },
        this.$emit('markinMarkoutPlayerListerner',this.markinMarkoutPlayerController);

        this.$on('clearOldMarkinMarkout',this.clearOldMarkinMarkoutValues);
        this.addWSEventListener({eventname:NotifierConstants.globalSettingsUpdatedNotification, callback:this.handleGlobalSettingsUpdateNotificationMarkinMarkout, parent:"MarkinMarkoutActions"});
    }
  },

  beforeDestroy(){
     if(!this.isAudioPlayerType){
        console.log('global event listener removed');
        //   window.removeEventListener('keydown', this.handleKeyBoardShortcuts);
        try{
            if(this.videoFrameCallbackID) {
                console.log(`Cancelled old [videoFrameCallback][${this.videoFrameCallbackID}]`);
                this.controller.player.cancelAnimationFrame(this.videoFrameCallbackID);
            }
        }catch(e){console.log(e)}
        this.$off('clearOldMarkinMarkout',this.clearOldMarkinMarkoutValues);
        this.removeWSEventListener({eventname:NotifierConstants.globalSettingsUpdatedNotification, callback:this.handleGlobalSettingsUpdateNotificationMarkinMarkout, parent:"MarkinMarkoutActions"});
     }
  },
  created(){
      if(this.isRequiredForAudioPlayer != null || this.isRequiredForAudioPlayer != undefined){
           this.isAudioPlayerType = this.isRequiredForAudioPlayer;
      }

      if(Utils.getFileMediaType(this.fileInfo) == 'audio'){
          this.markoutPlaceholderValue = this.formatDurationHHMMSS_withoutFrame(this.audioDuration);
      }
  },
  watch:{
      immediate:true,
      deep:true,
      playerObj: function(){
         if(!this.isAudioPlayerType)
            this.playerListener();
      },
  },
  computed:{
    globalSettingsFromStartup:{
      get(){
        return this.$store.state.startUpData.startUpDataDetails;
      }
    }
  }
}
</script>

<style scoped lang="scss">
.markin-markout-img {
  width: 114%;
  height: 100%;
  filter: brightness(0.6);
}
.clear-markin-markout:hover {
  text-decoration: underline;
  color: #747070;
}

.common-image-wrapper {
  position: relative;
}

.common-markin-markout-img {
  background-position: 50% 50% !important;
  filter: brightness(0.5);
  background-repeat: no-repeat !important;
  position: absolute;
  inset-inline-start:30%;
  top: 0;
  width: 40%;
  height: 100%;
}

input.mma-tc-input.invalid-duration-value {
    background: rgba(244, 67, 54, .2) !important;
    border: 1px solid #f44336 !important;
}

input.mma-tc-input.active-markin-markout-duration {
  background: #10585c !important;
  border: 1px solid #19868c !important;
}

/* ───────────────── wrapper ───────────────── */
.markin-markout-actions-wrapper {
  width: 100%;
  z-index: 2;
  position: relative;
  margin-top: 6px;
  padding: 0 !important;
  // overflow: hidden;
}

.mma-wrapper {
  background: #1a222c80;
  padding: 8px 10px;
  border-radius: 8px;
  width: 100%;
  box-sizing: border-box;
  // overflow: hidden;
}

/* ───────────────── first row layout ───────────────── */
.markin-markout-actions-wrapper .mma-row:not(.mma-row--advanced) {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 6px;
  flex-wrap: nowrap;
}

/* left / center / right containers */
.markin-markout-actions-wrapper .mma-row:not(.mma-row--advanced) > .actions-left,
.markin-markout-actions-wrapper .mma-row:not(.mma-row--advanced) > .actions-center,
.markin-markout-actions-wrapper .mma-row:not(.mma-row--advanced) > .actions-right {
  display: flex;
  align-items: center;
  min-width: 0;
}

/* left */
.markin-markout-actions-wrapper .mma-row:not(.mma-row--advanced) > .actions-left {
  flex: 0 0 auto;
  justify-content: flex-start;
}

/* center */
.markin-markout-actions-wrapper .mma-row:not(.mma-row--advanced) > .actions-center {
  flex: 1 1 auto;
  justify-content: center;
  gap: 6px;
  min-width: 0;
  padding-inline-start: 8px; /* moves center block a little left visually */
  overflow: hidden;
}

/* right */
.markin-markout-actions-wrapper .mma-row:not(.mma-row--advanced) > .actions-right {
  flex: 0 0 auto;
  justify-content: flex-end;
  gap: 6px;
  flex-wrap: nowrap;
  margin-inline-start: auto;
  min-width: max-content;
}

/* ───────────────── advanced row ───────────────── */
.mma-row--advanced {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 5px;
  margin-top: 6px;
  flex-wrap: wrap;
}

/* ───────────────── base button ───────────────── */
.mma-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 33px;
  // padding: 0 8px;
  gap: 4px;
  border-radius: 5px;
  border: 1px solid #3d4a5a;
  background: #2a3644;
  color: #c8d8e8;
  // font-size: 12px;
  font-weight: 500;
  font-family: inherit;
  white-space: nowrap;
  cursor: pointer;
  flex-shrink: 0;
  box-sizing: border-box;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
  line-height: 1;
  padding: 0 6px;
}

.mma-btn:hover {
  background: #344556;
  border-color: #4e6070;
  color: #fff;
}

.mma-btn:active {
  background: #1e2a38;
}

.mma-btn--active {
  border-color: #3d8bcd;
  color: #5aabf0;
}

/* optional existing class support */
.common-markinmarkout-action-btn {
  flex-shrink: 0;
}

/* ───────────────── TC display ───────────────── */
.mma-tc-display {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 33px;
  padding: 0 10px;
  border-radius: 5px;
  border: 1px solid #2d3947;
  background: #0d1218;
  font-size: 13px;
  font-weight: 600;
  color: #7db8d8;
  flex-shrink: 0;
  letter-spacing: 0.4px;
  box-sizing: border-box;
}

.mma-tc-label {
  color: #4a6278;
  font-size: 11px;
}

/* ───────────────── mark in / mark out group ───────────────── */
.mma-tc-group {
  display: inline-flex;
  align-items: stretch;
  height: 33px;
  border-radius: 5px;
  overflow: hidden;
  border: 1px solid #3a4757;
  box-sizing: border-box;
  min-width: 0;
  flex-shrink: 1;
}

.mma-tc-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  background: #1e2a38;
  border-inline-end: 1px solid #3a4757;
  font-size: 17px;
  line-height: 1;
  color: #7a9ab5;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s, color 0.15s;
  flex-shrink: 0;
}

.mma-tc-icon:hover {
  background: #253344;
  color: #d0dbe8;
}

.mma-tc-icon.right {
  border-inline-end: none;
  border-inline-start: 1px solid #3a4757;
}

.mma-tc-input {
  width: 80px;
  min-width: 60px;
  padding: 0 6px;
  border: none;
  outline: none;
  background: #0d1218;
  color: #e2ecf5;
  font-weight: 600;
  font-family: inherit;
  text-align: start;
  box-sizing: border-box;
  border-radius: 0 !important;
  box-shadow: none !important;
  height: 100%;
}

.mma-tc-input::placeholder {
  color: #3a4f63;
}

/* ───────────────── dropdown ───────────────── */
.mma-dropdown {
  position: relative;
  flex-shrink: 0;
}

.mma-dots {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #8aa0b8;
  padding: 0 2px;
  transition: color 0.15s;
}

.mma-dots:hover {
  color: #d0dbe8;
}

.mma-dropdown-menu {
  min-width: 180px !important;
  background: #1e2a38 !important;
  border: 1px solid #3a4757 !important;
  border-radius: 6px !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45) !important;
  padding: 0 !important;
}

.mma-dropdown-item {
  display: block;
  font-size: 13px;
  color: #c8d8e8 !important;
  cursor: pointer;
  padding: 8px 14px !important;
  border-bottom: 1px solid #2a3848;
  background: transparent !important;
  transition: background 0.15s;
}

.mma-dropdown-item:last-child {
  border-bottom: none;
}

.mma-dropdown-item:hover {
  background: #253344 !important;
  color: #fff !important;
}

.actions-left   { flex: 0 0 auto; }
.actions-center { flex: 1 1 auto; justify-content: center; padding-inline-start: 8px; overflow: hidden; }
.actions-right  { flex: 0 0 auto; margin-inline-start: auto; min-width: max-content; }
</style>